import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  EscapeHatchState,
  SupportedCurrency,
} from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconClock } from '#src/atoms/icons/system/iconClock.ts';
import { iconInfoCircle } from '#src/atoms/icons/system/iconInfoCircle.ts';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM } from '#src/atoms/typography/index.tsx';
import { AmountWithConversion } from '#src/components/buyActions/AmountWithConversion/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { HELP } from '#src/constants/__generated__/routes.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useSnackNotificationContext } from '#src/contexts/snackNotification/index.ts';
import { useWalletContext } from '#src/contexts/wallet/index.ts';
import { useIsLaptop } from '#src/hooks/device/useIsLaptop.ts';
import { useMutation } from '#src/hooks/graphql/useMutation.ts';
import { useQuery } from '#src/hooks/graphql/useQuery.ts';
import { glossary } from '#src/lib/glossary.ts';
import { formatGqlErrors } from '#src/lib/gql/index.ts';

import type {
  CreateStarkwareEscapeHatchMutation,
  CreateStarkwareEscapeHatchMutationVariables,
  StarkwareEscapeHatchQuery,
  StarkwareEscapeHatchQueryVariables,
} from './__generated__/index.graphql.ts';

const STARKWARE_ESCAPE_HATCH_QUERY = gql`
  query StarkwareEscapeHatchQuery {
    config {
      id
      starkwareEscapeHatchEnabled
      starkwareEscapeHatchFeeAmount
      starkwareEscapeHatchFeeRecipient
    }
    currentUser {
      slug
      availableBalanceForWithdrawal
      starkwareEscapeHatch {
        id
        state
        destination
        requiresRegistration
      }
    }
  }
` as TypedDocumentNode<
  StarkwareEscapeHatchQuery,
  StarkwareEscapeHatchQueryVariables
>;

const CREATE_STARKWARE_ESCAPE_HATCH_MUTATION = gql`
  mutation CreateStarkwareEscapeHatchMutation(
    $input: createStarkwareEscapeHatchInput!
  ) {
    createStarkwareEscapeHatch(input: $input) {
      escapeHatch {
        id
        state
        destination
        requiresRegistration
      }
      currentUser {
        slug
        shouldMigrateEth
      }
      errors {
        code
        message
      }
    }
  }
` as TypedDocumentNode<
  CreateStarkwareEscapeHatchMutation,
  CreateStarkwareEscapeHatchMutationVariables
>;

const CtaWrapper = styled.div`
  padding: var(--unit);
  border-top: 1px solid var(--c-nd-150);
`;
const DialogBody = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--double-unit);
  text-align: center;
`;
const Actions = styled.div`
  display: flex;
  gap: var(--unit);
`;
const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--unit);
`;

type Variant = 'idle' | 'pending' | 'failed';

export const StarkwareEscapeHatch = () => {
  const isLaptop = useIsLaptop();
  const { currentUser } = useCurrentUserContext();
  const { getEscapeHatchSignatures } = useWalletContext();
  const { showNotification } = useSnackNotificationContext();

  const { data } = useQuery(STARKWARE_ESCAPE_HATCH_QUERY, {
    skip: !currentUser,
  });

  const [createEscapeHatch, { loading: creating }] = useMutation(
    CREATE_STARKWARE_ESCAPE_HATCH_MUTATION
  );

  const [open, setOpen] = useState(false);

  const config = data?.config;
  const escapeHatch = data?.currentUser?.starkwareEscapeHatch ?? null;
  const availableWei = data?.currentUser?.availableBalanceForWithdrawal ?? '0';
  const escapeHatchEnabled = !!config?.starkwareEscapeHatchEnabled;
  const shouldMigrateEth = !!currentUser?.shouldMigrateEth;

  const variant: Variant | null = (() => {
    if (shouldMigrateEth) return 'idle';
    if (escapeHatch?.state === EscapeHatchState.CREATED) return 'pending';
    if (escapeHatch?.state === EscapeHatchState.FAILED) return 'failed';
    return null;
  })();

  if (!variant) return null;

  const confirmMigration = async () => {
    if (!config) return;
    const signatures = await getEscapeHatchSignatures({
      fee: BigInt(config.starkwareEscapeHatchFeeAmount),
      feeRecipient: config.starkwareEscapeHatchFeeRecipient,
    });
    if (!signatures) return;

    const { errors } = await createEscapeHatch({
      variables: {
        input: {
          destination: signatures.ethAddress as EthereumAddress,
          registrationSignature: signatures.registerEthAddressSignature,
          withdrawWithFeeSignature: signatures.withdrawWithFeeSignature,
          withdrawWithFeeToBaseBankSignature:
            signatures.withdrawWithFeeToBaseBankSignature,
        },
      },
    });
    if (errors.length) {
      showNotification('errors', { errors: formatGqlErrors(errors) });
      return;
    }
    setOpen(false);
  };

  const ctaSize = isLaptop ? 'compact' : 'small';
  const button = (() => {
    switch (variant) {
      case 'pending':
        return (
          <Button
            fullWidth
            color="caution"
            size={ctaSize}
            startIcon={<FontAwesomeIcon icon={iconClock} />}
            onClick={() => setOpen(true)}
          >
            <FormattedMessage defaultMessage="Migrating ETH" />
          </Button>
        );
      case 'failed':
        return (
          <Button
            fullWidth
            color="destructive"
            size={ctaSize}
            startIcon={<FontAwesomeIcon icon={iconInfoCircle} />}
            onClick={() => setOpen(true)}
          >
            <FormattedMessage defaultMessage="Migration Error" />
          </Button>
        );
      case 'idle':
      default:
        return (
          <Button
            fullWidth
            color="destructive"
            size={ctaSize}
            startIcon={<FontAwesomeIcon icon={iconInfoCircle} />}
            onClick={() => setOpen(true)}
          >
            <FormattedMessage defaultMessage="Migrate ETH" />
          </Button>
        );
    }
  })();

  return (
    <>
      <CtaWrapper>{button}</CtaWrapper>
      <Dialog
        maxWidth="xs"
        fullWidth
        fullScreen={false}
        hideHeader
        open={open}
        onClose={() => setOpen(false)}
      >
        <DialogBody>
          {variant === 'idle' && !escapeHatchEnabled && (
            <>
              <BodyM bold>
                <FormattedMessage defaultMessage="Migration under maintenance" />
              </BodyM>
              <BodyM>
                <FormattedMessage defaultMessage="ETH migration is temporarily unavailable while we perform maintenance. It should be back online before June 5th." />
              </BodyM>
              <Actions>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage {...glossary.close} />
                </Button>
                <Button
                  fullWidth
                  size="medium"
                  color="primary"
                  to={HELP}
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage defaultMessage="Contact support" />
                </Button>
              </Actions>
            </>
          )}
          {!!(variant === 'idle' && escapeHatchEnabled && config) && (
            <>
              <BodyM bold>
                <FormattedMessage defaultMessage="Migrate your ETH" />
              </BodyM>
              <BodyM>
                <FormattedMessage defaultMessage="Confirm migration of your ETH balance to your Ethereum address." />
              </BodyM>
              <Row>
                <BodyM color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Amount to migrate" />
                </BodyM>
                <AmountWithConversion
                  monetaryAmount={{
                    referenceCurrency: SupportedCurrency.WEI,
                    wei: availableWei,
                  }}
                />
              </Row>
              <Row>
                <BodyM color="var(--c-nd-600)">
                  <FormattedMessage defaultMessage="Network fee" />
                </BodyM>
                <AmountWithConversion
                  monetaryAmount={{
                    referenceCurrency: SupportedCurrency.WEI,
                    wei: config.starkwareEscapeHatchFeeAmount,
                  }}
                />
              </Row>
              <Actions>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage {...glossary.cancel} />
                </Button>
                <Button
                  fullWidth
                  size="medium"
                  color="primary"
                  onClick={confirmMigration}
                  loading={creating}
                >
                  <FormattedMessage {...glossary.confirm} />
                </Button>
              </Actions>
            </>
          )}
          {variant === 'pending' && (
            <>
              <BodyM bold>
                <FormattedMessage defaultMessage="Migration in progress" />
              </BodyM>
              <BodyM>
                <FormattedMessage defaultMessage="Your ETH migration is being processed on-chain. This can take up to a few hours." />
              </BodyM>
              <Button
                size="medium"
                color="primary"
                onClick={() => setOpen(false)}
              >
                <FormattedMessage {...glossary.close} />
              </Button>
            </>
          )}
          {variant === 'failed' && (
            <>
              <BodyM bold>
                <FormattedMessage defaultMessage="Migration failed" />
              </BodyM>
              <BodyM>
                <FormattedMessage defaultMessage="Something went wrong while migrating your ETH. Please contact support so we can help you recover your funds." />
              </BodyM>
              <Actions>
                <Button
                  fullWidth
                  size="medium"
                  color="tertiary"
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage {...glossary.close} />
                </Button>
                <Button
                  fullWidth
                  size="medium"
                  color="primary"
                  to={HELP}
                  onClick={() => setOpen(false)}
                >
                  <FormattedMessage defaultMessage="Contact support" />
                </Button>
              </Actions>
            </>
          )}
        </DialogBody>
      </Dialog>
    </>
  );
};
